<template>
  <LoginWrapper>
    <template v-if="!loading">
      <RaceHeadline
        :title="race.title"
        :rating="race.rating"
        :potentialFlip="race.potentialFlip"
        :potentialFlipDemToGop="race.potentialFlipDemToGop"
      />

      <RaceInfo
        :currentParty="race.subtitle.currentParty"
        :isContested="race.subtitle.isContested"
        :openSeat="race.subtitle.openSeat"
        :PVI="race.subtitle.PVI"
      />

      <MainCandidates
        :isContested="race.subtitle.isContested"
        :mainRepublicanCandidate="race.mainRepublicanCandidate"
        :mainDemocratCandidate="race.mainDemocratCandidate"
        :otherCandidates="race.otherCandidates"
      />

      <PartyChances
        :electionDate="race.electionDate"
        :isContested="race.subtitle.isContested"
        :republicanChanceOfWinning="race.percentChanceOfWinning.republicans"
        :democratChanceOfWinning="race.percentChanceOfWinning.democrats"
      />

      <ChanceOfWinningChart
        v-if="race.subtitle.isContested"
        :name="'gopWinProbability'"
        :prop="'gopWinProbability'"
        :data="race.chanceOfWinningChart"
        :chamber="race.chamber.toLowerCase()"
        :currentDate="chartCurrentDate"
        :electionDay="race.electionDate"
        :endDate="chartEndDate"
        :key="`chanceOfWinning-${chartKey}`"
        @on-mouseout="chartKey += 1"
        :scoreboardData="scoreboardData"
        @scoreboard-update="scoreboardData = $event"
      />

      <!--
      <RaceMap
        :stateFips="race.map.stateFips"
        :topojsonUrl="race.map.topojsonUrl"
        :vcuData="race.map.vcus"
      />
      -->

      <h2 class="mt-8">More About Our Model</h2>

      <RaceNotes
        :lastUpdated="race.notes.lastUpdated"
        :notes="race.notes.notes"
      />

      <PollingTable
        :pollAverage="race.polling.pollAverage"
        :tableItems="race.polling.pollsters"
      />

      <CandidateFinancesTable :tableItems="race.candidateFinancesTable.candidates" />

      <template v-if="race.chamber.toLowerCase() === 'senate'">
        <div class="table-container">
          <HistoricalRacesTable
            :tableTitle="`Presidential ${race.presidentialTable.year}`"
            :totalVotes="race.presidentialTable.totalVotes"
            :tableItems="race.presidentialTable.candidates"
            :chamber="race.chamber"
          />
          <HistoricalRacesTable
            :tableTitle="`Senate ${race.senateTable.year}`"
            :totalVotes="race.senateTable.totalVotes"
            :tableItems="race.senateTable.candidates"
            :chamber="race.chamber"
          />
        </div>
        <p class="last-updated"><b>Last updated:</b> {{ new Date(race.electionDate).toLocaleString() }}</p>
      </template>
    </template>
  </LoginWrapper>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import { LoginWrapper } from "@/components/common";
import RaceHeadline from "./components/RaceHeadline";
import RaceInfo from "./components/RaceInfo";
import MainCandidates from "./components/MainCandidates";
import ChanceOfWinningChart from "./components/ChanceOfWinningChart";
// import RaceMap from "./components/RaceMap";
import PollingTable from "./components/PollingTable";

const components = {
  LoginWrapper,
  RaceHeadline,
  RaceInfo,
  MainCandidates,
  PartyChances: () => import("./components/PartyChances"),
  ChanceOfWinningChart,
  // RaceMap,
  RaceNotes: () => import("./components/RaceNotes"),
  PollingTable,
  HistoricalRacesTable: () => import("./components/HistoricalRacesTable"),
  CandidateFinancesTable: () => import("./components/CandidateFinancesTable"),
};

const computed = {
  ...mapGetters({
    race: "race/raceData",
  }),
  chartEndDate() {
    const fixedElectionDate = this.$options.filters.fixDayOffByOne(this.race.electionDate);
    const electionDate = new Date(fixedElectionDate);
    const daysToAdd = 5;
    const endDate = new Date(electionDate.setDate(electionDate.getDate() + daysToAdd));

    return endDate;
  },
  chartCurrentDate() {
    const dates = this.race.chanceOfWinningChart.map((c) => c.date);
    const lastDate = dates.pop();
    const fixedLastDate = new Date(this.$options.filters.fixDayOffByOne(lastDate));

    return fixedLastDate;
  },
};

const methods = {
  ...mapActions({
    fetchRaceData: "race/fetchRaceData",
  }),
  isValidRaceID(raceId) {
    if (!raceId) return false;
    if (typeof raceId !== "string") return false;
    // if (raceId.length !== 9 || raceId.length !== 11) return false;
    if (raceId[0].toLowerCase() !== "s" && raceId[0].toLowerCase() !== "h") return false;

    return true;
  },
  async fetchData(newRaceId = null) {
    const raceId = newRaceId || this.$route.params.raceID || null;

    if (!this.isValidRaceID(raceId)) {
      this.loading = false;
      // eslint-disable-next-line no-console
      console.error(`Unable to fetch race ${raceId}`);
      return;
    }

    this.raceId = raceId;
    await this.fetchRaceData(raceId);
    this.loading = false;
  },
};

export default {
  components,
  computed,
  methods,
  data: () => ({
    loading: true,
    chartKey: 0,
    scoreboardData: null,
    raceId: null,
  }),
  metaInfo: {
    title: "2022 Race Forecast",
  },
  created() {
    this.$watch(
      () => this.$route.params,
      () => {
        this.fetchData();
      },
      { immediate: true }
    );
  },
};
</script>

<style>
.table-container {
  display: flex;
  flex-direction: column;
  gap: 4em;
  margin-top: 4em;
}

.last-updated {
  font-size: .8rem;
  font-style: italic;
  margin: 0;
  text-align: right;
}
</style>